import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Users, Wallet, TrendingUp, Activity, UserPlus, Send, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, StatusBadge } from "@/components/common";
import { useApi } from "@/lib/api";
import { money, dateTime, TX_LABEL } from "@/lib/format";

const Kpi = ({ icon: Icon, label, value, hint, testId, i }) => (
  <div className={`rise rise-${i} rounded-2xl border border-slate-200 bg-white p-6 transition-shadow hover:shadow-md`}>
    <div className="flex items-center justify-between">
      <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
      <span className="rounded-lg bg-teal-50 p-2 text-teal-700"><Icon className="h-4 w-4" /></span>
    </div>
    <p data-testid={testId} className="mt-4 font-display text-2xl font-semibold tracking-tight text-slate-900">{value}</p>
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </div>
);

export default function Dashboard() {
  const api = useApi();
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
  }, [api]);
  if (!d) return <div className="h-64 animate-pulse rounded-2xl bg-slate-100" />;
  return (
    <>
      <PageHeader title="Panel general" subtitle="Resumen de la operación de e-Plata vía STP">
        <Button asChild data-testid="btn-alta-cliente" className="bg-teal-600 hover:bg-teal-700"><Link to="/clientes/nuevo"><UserPlus className="mr-2 h-4 w-4" />Alta de cliente</Link></Button>
        <Button asChild variant="outline" data-testid="btn-nueva-transferencia"><Link to="/transferencias"><Send className="mr-2 h-4 w-4" />Nueva transferencia</Link></Button>
      </PageHeader>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Kpi i={1} icon={Users} label="Clientes" value={d.clientes} hint={`${d.pm} PM · ${d.pf} PF · ${d.kyc_pendientes} KYC por revisar`} testId="kpi-clientes" />
        <Kpi i={2} icon={Wallet} label="Saldo total" value={money(d.saldo_total)} hint={`${d.cuentas} cuentas CLABE`} testId="kpi-saldo-total" />
        <Kpi i={3} icon={TrendingUp} label="Volumen del mes" value={money(d.volumen_mes)} hint="SPEI liquidados" testId="kpi-volumen-mes" />
        <Kpi i={4} icon={Activity} label="Transacciones hoy" value={d.tx_hoy} testId="kpi-tx-hoy" />
      </div>
      <div className="grid gap-6 lg:grid-cols-5">
        <div className="rise rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-3">
          <h3 className="text-lg font-semibold text-slate-900">Flujo SPEI · últimos 14 días</h3>
          <div className="mt-4 h-72" data-testid="chart-flujo">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.serie}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="dia" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => money(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="entradas" name="Entradas" fill="#0D9488" radius={[4, 4, 0, 0]} />
                <Bar dataKey="salidas" name="Salidas" fill="#0F172A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rise rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">Movimientos recientes</h3>
            <Link to="/transacciones" className="text-sm text-teal-700 hover:underline" data-testid="link-ver-transacciones">Ver todo</Link>
          </div>
          <ul className="mt-4 divide-y divide-slate-100">
            {d.recientes.length === 0 && <li className="py-6 text-sm text-slate-500">Sin movimientos</li>}
            {d.recientes.map((t) => (
              <li key={t.id} className="flex items-center gap-3 py-3" data-testid={`recent-tx-${t.id}`}>
                <span className={`rounded-full p-2 ${t.tipo === "entrada" ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-700"}`}>
                  {t.tipo === "entrada" ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900">{t.client_nombre}</p>
                  <p className="truncate text-xs text-slate-500">{t.contraparte_nombre} · {dateTime(t.created_at)}</p>
                </div>
                <div className="text-right">
                  <p className={`font-mono text-sm ${t.tipo === "entrada" ? "text-teal-700" : "text-slate-900"}`}>{t.tipo === "entrada" ? "+" : "-"}{money(t.monto)}</p>
                  <StatusBadge status={t.estado} label={TX_LABEL[t.estado]} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}
